import React, { useLayoutEffect, useRef, useState } from 'react';
import { Box, Text } from '@mantine/core';
import { useDroppable } from '@dnd-kit/core';
import type { PropEntry } from '../../../data/ShellConfig';
import { SCENE_TEMPLATES, type SceneTemplateId, type ShellComponent } from '../../../data/ShellConfig';
import { buildComponentPreviewUrl, isStaticImageUrl, isStaticVideoUrl } from './studioUtils';
import { dragIds } from './dragTypes';

export type ScenePanelDraft = {
    instanceId?: string;
    componentId: string | null;
    propOverrides: PropEntry[];
};

type ScenePreviewFrameProps = {
    templateId: SceneTemplateId;
    panels: ScenePanelDraft[];
    components: ShellComponent[];
    /**
     * live: full hero preview with device bezel + shadow, renders real media.
     * thumb: small framed thumbnail, renders panel labels only.
     * tile: chromeless fill (no bezel/shadow), renders real media — for embedding
     *   inside a card that already provides the frame, e.g. the playlist strip.
     */
    variant: 'live' | 'thumb' | 'tile';
    droppable?: boolean;
    activeDropPanelIndex?: number | null;
    /** Label shown above the frame, mirroring the device config Screen Preview. */
    label?: string;
    /** Sticks the frame to the top of the viewport while scrolling, like the device config preview. */
    sticky?: boolean;
    /** Overrides the default max width (280px) for non-live variants, e.g. smaller library tiles. */
    maxWidth?: number;
};

const PREVIEW_REF_W = 1080;
const PREVIEW_REF_H = 1920;

const ScaledIframePanel: React.FC<{ src: string; label: string }> = ({ src, label }) => {
    const containerRef = useRef<HTMLDivElement>(null);
    const [scale, setScale] = useState(1);

    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return undefined;
        const measure = () => {
            const w = el.clientWidth;
            const h = el.clientHeight;
            setScale(Math.min(w / PREVIEW_REF_W, h / PREVIEW_REF_H));
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    return (
        <Box ref={containerRef} style={{ width: '100%', height: '100%', position: 'relative', overflow: 'hidden' }}>
            <Box
                style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: PREVIEW_REF_W,
                    height: PREVIEW_REF_H,
                    transform: `scale(${scale})`,
                    transformOrigin: 'top left',
                    pointerEvents: 'none',
                }}
            >
                <iframe
                    title={label}
                    src={src}
                    style={{
                        width: PREVIEW_REF_W,
                        height: PREVIEW_REF_H,
                        border: 0,
                        display: 'block',
                        background: '#111',
                    }}
                />
            </Box>
        </Box>
    );
};

// Mirrors backend/resources/device-shell/IframePool.tsx: direct image/video URLs are rendered as
// native <img>/<video> with object-fit: contain rather than the scaled-iframe trick above, since
// raster media scales natively via CSS to fit the panel — no reference frame or transform needed.
const mediaFitStyle: React.CSSProperties = {
    width: '100%',
    height: '100%',
    display: 'block',
    objectFit: 'contain',
    objectPosition: 'center',
};

const MediaFitPanel: React.FC<{ src: string; isVideo: boolean; label: string }> = ({
    src,
    isVideo,
    label,
}) =>
    isVideo ? (
        <video src={src} autoPlay muted loop playsInline style={mediaFitStyle} />
    ) : (
        <img src={src} alt={label} style={mediaFitStyle} />
    );

const DroppablePanel: React.FC<{
    index: number;
    heightPercent: number;
    droppable: boolean;
    isActiveDrop: boolean;
    children: React.ReactNode;
}> = ({ index, heightPercent, droppable, isActiveDrop, children }) => {
    const { setNodeRef, isOver } = useDroppable({
        id: dragIds.panel(index),
        disabled: !droppable,
    });

    return (
        <Box
            ref={setNodeRef}
            style={{
                height: `${heightPercent}%`,
                minHeight: 0,
                position: 'relative',
                overflow: 'hidden',
                outline:
                    droppable && (isOver || isActiveDrop)
                        ? '2px dashed var(--mantine-color-violet-5)'
                        : '1px solid rgba(255,255,255,0.08)',
                background: '#0d0d0d',
            }}
        >
            {children}
        </Box>
    );
};

const ScenePreviewFrame: React.FC<ScenePreviewFrameProps> = ({
    templateId,
    panels,
    components,
    variant,
    droppable = false,
    activeDropPanelIndex = null,
    label,
    sticky = false,
    maxWidth,
}) => {
    const template = SCENE_TEMPLATES.find(t => t.id === templateId) ?? SCENE_TEMPLATES[0];
    const isLive = variant === 'live';
    const isTile = variant === 'tile';
    // Both live and tile render real component media; thumb shows labels only.
    const renderMedia = isLive || isTile;

    return (
        <Box
            style={
                sticky
                    ? { position: 'sticky', top: 16 }
                    : isTile
                      ? { width: '100%', height: '100%' }
                      : undefined
            }
        >
            {label && (
                <Text size="sm" fw={500} c="dimmed" mb="xs">
                    {label}
                </Text>
            )}
            <Box
                style={
                    isTile
                        ? { background: '#080808', width: '100%', height: '100%' }
                        : {
                              background: 'linear-gradient(160deg, #2a2a2a 0%, #1a1a1a 100%)',
                              borderRadius: 8,
                              padding: isLive ? 6 : 8,
                              // The live frame must have an explicit width: its panels can render an
                              // absolutely positioned iframe (for scaling) with no in-flow content, so
                              // a shrink-to-fit width would collapse to ~0 once every panel is populated.
                              width: isLive ? '100%' : undefined,
                              maxWidth: isLive ? undefined : (maxWidth ?? 280),
                              boxShadow: isLive
                                  ? '0 16px 48px rgba(0,0,0,0.5), inset 0 1px 0 rgba(255,255,255,0.08), inset 0 0 0 1px rgba(255,255,255,0.05)'
                                  : undefined,
                          }
                }
            >
                <Box
                    style={{
                        background: '#080808',
                        ...(isTile
                            ? { width: '100%', height: '100%' }
                            : { aspectRatio: '9 / 16' }),
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 2,
                        borderRadius: isTile ? 0 : 4,
                        overflow: 'hidden',
                        boxShadow: isLive ? 'inset 0 0 0 1px rgba(255,255,255,0.06)' : undefined,
                    }}
                >
                    {template.panels.map((heightPercent, index) => {
                        const panel = panels[index];
                        const component = components.find(c => c.id === panel?.componentId);
                        const panelLabel = component?.name ?? `Panel ${index + 1}`;

                        return (
                            <DroppablePanel
                                key={index}
                                index={index}
                                heightPercent={heightPercent}
                                droppable={droppable}
                                isActiveDrop={activeDropPanelIndex === index}
                            >
                                {component && renderMedia ? (
                                    isStaticImageUrl(component.url) || isStaticVideoUrl(component.url) ? (
                                        <MediaFitPanel
                                            src={buildComponentPreviewUrl(component, panel?.propOverrides)}
                                            isVideo={isStaticVideoUrl(component.url)}
                                            label={panelLabel}
                                        />
                                    ) : (
                                        <ScaledIframePanel
                                            src={buildComponentPreviewUrl(component, panel?.propOverrides)}
                                            label={panelLabel}
                                        />
                                    )
                                ) : component ? (
                                    <Box
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            padding: 8,
                                            background:
                                                index % 2 === 0
                                                    ? 'var(--mantine-color-blue-9)'
                                                    : 'var(--mantine-color-violet-9)',
                                        }}
                                    >
                                        <Text size="xs" c="white" ta="center" lineClamp={3}>
                                            {component.name}
                                        </Text>
                                    </Box>
                                ) : (
                                    <Box
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            border: '1px dashed rgba(255,255,255,0.2)',
                                        }}
                                    >
                                        <Text size="xs" c="dimmed">
                                            No component
                                        </Text>
                                    </Box>
                                )}
                            </DroppablePanel>
                        );
                    })}
                </Box>
            </Box>
        </Box>
    );
};

export default ScenePreviewFrame;
